/**
 * Permissions Type System
 * 
 * Implements granular, scoped permissions with hierarchical inheritance.
 * Runtime authorization uses capability IDs only (no legacy role names).
 * 
 * Key concepts:
 * - PermissionAction: Specific actions a user can perform (e.g., 'items.create')
 * - ScopeKind: The type of scope (organization, group, process, session, item)
 * - PermissionScope: A specific instance of a scope with optional parent chain
 * - EffectivePermission: The resolved permission at a specific scope
 * 
 * Inheritance Rule: "Most Specific Wins"
 * - Permissions are resolved from most specific scope to least specific
 * - If no permission is found at a specific scope, inherit from parent
 */

import type { CapabilityId } from './capabilityIds';
import { getCapabilitiesForAction } from './capabilityIds';

// ============================================================================
// Permission Actions
// ============================================================================

/**
 * All granular permission actions in the system.
 * Format: "{resource}.{action}"
 */
export type PermissionAction =
  // Items permissions
  | 'items.view'
  | 'items.create'
  | 'items.triage'
  | 'items.triage.approve'
  | 'items.triage.route'
  | 'items.triage.attach_relationships'
  | 'items.manage_ownership'
  | 'items.edit'
  | 'items.delete'
  | 'items.manage_deduplication'
  | 'items.categorize'
  | 'items.request_clarity'
  | 'items.select'
  | 'items.approve_selection'
  | 'items.approve_ai_provenance'
  // Relationships permissions
  | 'relationships.create'
  | 'relationships.edit'
  | 'relationships.delete'
  | 'relationships.resolve'
  // Process / Cycle permissions
  | 'process.view'
  | 'process.create'
  | 'process.edit'
  | 'process.prioritize'
  | 'process.publish'
  | 'process.advance_phase'
  | 'process.legitimacy_advance'
  | 'cycle.pool.add'
  | 'cycle.pool.remove'
  | 'cycle.abort'
  // Track permissions
  | 'tracks.create'
  | 'tracks.configure'
  | 'track.set_official_result'
  // Session permissions
  | 'session.view'
  | 'session.facilitate'
  | 'session.record'
  | 'session.participate'
  | 'session.vote'
  | 'session.pool'
  | 'session.pool.add'
  | 'session.pool.remove'
  | 'session.publish'
  | 'session.run_ranking'
  | 'session.view_results'
  // S-33 (mig 891): change a session's ranking order (and its cut line) after the run
  | 'ranking.override'
  | 'session.schedule'
  | 'session.set_open'
  | 'session.tools.select'
  | 'session.tools.configure'
  // Pool permissions
  | 'pools.select'
  | 'pools.lock'
  | 'pools.reopen'
  // Focus List / Item Pool permissions
  | 'lists.view'
  | 'lists.create'
  | 'lists.edit'
  | 'lists.move_cutline'
  | 'lists.publish'
  | 'lists.approve'
  // Criteria permissions
  | 'criteria.view'
  | 'criteria.create'
  | 'criteria.edit'
  | 'criteria.delete'
  | 'criteria.select'
  | 'criteria.weight'
  | 'criteria.lock'
  | 'criteria.reopen'
  | 'criteria.define_custom_meaning'
  // Values & Value Ranges permissions
  | 'values.propose'
  | 'values.accept'
  | 'values.reject'
  | 'values.override'
  | 'values.define_range'
  | 'values.approve_range'
  | 'values.define_scales'
  | 'values.converge'
  // Attributes permissions
  | 'attributes.define'
  | 'attributes.assign_owner'
  // Type lists (migration 931)
  | 'type_lists.edit'
  // Catchball permissions
  | 'catchball.request'
  | 'catchball.propose'
  | 'catchball.return'
  | 'catchball.accept_provisional'
  | 'catchball.accept'
  | 'catchball.reject'
  | 'catchball.rollout'
  | 'catchball.aggregate'
  | 'catchball.adjust_path'
  // Analytics permissions
  | 'analytics.view'
  | 'analytics.export'
  // Drift / Plan-of-Record permissions
  | 'drift.flag'
  | 'drift.monitor'
  // Workflow permissions
  | 'workflow.create'
  | 'workflow.edit'
  | 'workflow.delete'
  | 'workflow.archive'
  | 'workflow.set_default'
  | 'workflow.assign'
  | 'workflow.use'
  | 'workflow.manage_templates'
  // Task permissions
  | 'task.assign'       // Assign a task to another user
  | 'task.self_assign'  // Assign a task to yourself
  // Decision rights permissions
  | 'decision_rights.assign'      // Assign or change who owns a decision right on a cycle
  | 'decision_rights.delegate'    // Delegate a held decision right to another principal
  | 'decision_rights.configure'   // Configure execution modes and delegation constraints
  // Admin permissions
  | 'admin.users'
  | 'admin.groups'
  | 'admin.roles'
  | 'admin.policies'
  | 'admin.settings'
  | 'admin.grant_workspace'
  | 'admin.grant_tenant';

/**
 * Permission action categories for grouping in UI
 */
export type PermissionCategory = 
  | 'items' 
  | 'process' 
  | 'tracks'
  | 'session' 
  | 'pools'
  | 'lists' 
  | 'criteria' 
  | 'values'
  | 'attributes'
  | 'catchball'
  | 'workflow'
  | 'analytics'
  | 'decision_rights'
  | 'tasks'
  | 'drift'
  | 'admin';

/**
 * Permission definition for display/management
 */
export interface PermissionDefinition {
  id: PermissionAction;
  category: PermissionCategory;
  label: string;
  description: string;
}

/**
 * All permission definitions
 */
export const PERMISSION_DEFINITIONS: Record<PermissionAction, PermissionDefinition> = {
  // Items
  'items.view': { id: 'items.view', category: 'items', label: 'View Items', description: 'Can view items in the workspace' },
  'items.create': { id: 'items.create', category: 'items', label: 'Create Items', description: 'Can create new items' },
  'items.triage': { id: 'items.triage', category: 'items', label: 'Triage Items', description: 'Can triage intake items and decide whether to pursue, defer, reject, redirect, or request clarification' },
  'items.triage.approve': { id: 'items.triage.approve', category: 'items', label: 'Approve Triage Outcome', description: 'Can record a triage decision (pursue, defer, reject, or needs clarification) for an intake item' },
  'items.triage.route': { id: 'items.triage.route', category: 'items', label: 'Route Item to Queue', description: 'Can redirect an intake item to a different org-unit queue during triage' },
  'items.triage.attach_relationships': { id: 'items.triage.attach_relationships', category: 'items', label: 'Attach Relationships During Triage', description: 'Can attach canonical relationships (cluster, package, exclusive, dependency, aggregation, elaboration, lineage) to an item while triaging it' },
  'items.manage_ownership': { id: 'items.manage_ownership', category: 'items', label: 'Manage Item Ownership', description: 'Can transfer ownership of an item to another user, or delegate the item while retaining ownership' },
  'items.edit': { id: 'items.edit', category: 'items', label: 'Edit Items', description: 'Can edit existing items' },
  'items.delete': { id: 'items.delete', category: 'items', label: 'Delete Items', description: 'Can delete items' },
  'items.approve_ai_provenance': { id: 'items.approve_ai_provenance', category: 'items', label: 'Approve AI-Drafted Items', description: 'Can review and approve items whose title/description were AI-drafted, transitioning them from AI Draft to AI Approved' },
  'relationships.create': { id: 'relationships.create', category: 'items', label: 'Create Relationships', description: 'Can create clusters, packages, exclusive sets, dependencies, aggregations, and elaborations between items' },
  'relationships.edit': { id: 'relationships.edit', category: 'items', label: 'Edit Relationships', description: 'Can rename and update metadata on existing relationship groups and edges' },
  'relationships.delete': { id: 'relationships.delete', category: 'items', label: 'Delete Relationships', description: 'Can dissolve relationship groups and edges, and remove members from groups' },
  // Process / Cycle
  'process.view': { id: 'process.view', category: 'process', label: 'View Cycles', description: 'Can view DEGAP cycles and processes' },
  'process.create': { id: 'process.create', category: 'process', label: 'Create Cycles', description: 'Can create new prioritization cycles' },
  'process.edit': { id: 'process.edit', category: 'process', label: 'Edit Cycles', description: 'Can edit cycle configuration' },
  'process.prioritize': { id: 'process.prioritize', category: 'process', label: 'Participate in Prioritization', description: 'Can participate in prioritization activities' },
  'process.publish': { id: 'process.publish', category: 'process', label: 'Publish Cycle Results', description: 'Can publish cycle results' },
  'process.advance_phase': { id: 'process.advance_phase', category: 'process', label: 'Advance Cycle Phase', description: 'Can advance a cycle from one phase to the next' },
  'process.legitimacy_advance': { id: 'process.legitimacy_advance', category: 'process', label: 'Legitimacy Advance', description: 'Can advance items through legitimacy gates (focus, lock, publish)' },
  'cycle.pool.add': { id: 'cycle.pool.add', category: 'process', label: 'Add to Cycle Pool', description: 'Can add items to a cycle pool' },
  'cycle.pool.remove': { id: 'cycle.pool.remove', category: 'process', label: 'Remove from Cycle Pool', description: 'Can remove items from a cycle pool' },
  'cycle.abort': { id: 'cycle.abort', category: 'process', label: 'Abort Cycle', description: 'Can abort and cancel an active cycle' },
  // Tracks
  'tracks.create': { id: 'tracks.create', category: 'tracks', label: 'Create Tracks', description: 'Can create new tracks within a cycle' },
  'tracks.configure': { id: 'tracks.configure', category: 'tracks', label: 'Configure Tracks', description: 'Can configure track settings, criteria, and participants' },
  'track.set_official_result': { id: 'track.set_official_result', category: 'tracks', label: 'Choose Track Result', description: "Can choose which published session result is the track's result" },
  // Sessions
  'session.view': { id: 'session.view', category: 'session', label: 'View Sessions', description: 'Can view prioritization sessions' },
  'session.facilitate': { id: 'session.facilitate', category: 'session', label: 'Facilitate Sessions', description: 'Can facilitate and run live sessions' },
  'session.record': { id: 'session.record', category: 'session', label: 'Record Decisions', description: 'Can record and capture session decisions' },
  'session.participate': { id: 'session.participate', category: 'session', label: 'Participate in Sessions', description: 'Can participate in prioritization decisions' },
  'session.vote': { id: 'session.vote', category: 'session', label: 'Vote in Sessions', description: 'Can cast votes on values and rankings' },
  'session.pool': { id: 'session.pool', category: 'session', label: 'Manage Session Pool', description: 'Base permission for session pool operations — resolved by the v1 evaluator from session.pool.add / session.pool.remove' },
  'session.pool.add': { id: 'session.pool.add', category: 'session', label: 'Add to Session Pool', description: 'Can add items to a session pool' },
  'session.pool.remove': { id: 'session.pool.remove', category: 'session', label: 'Remove from Session Pool', description: 'Can remove items from a session pool' },
  'session.publish': { id: 'session.publish', category: 'session', label: 'Publish Session Results', description: 'Can publish session results' },
  'session.run_ranking': { id: 'session.run_ranking', category: 'session', label: 'Run Ranking Tool', description: 'Can run ranking/prioritization tools within a session' },
  'session.view_results': { id: 'session.view_results', category: 'session', label: 'View Session Results', description: 'Can view ranking results and scores' },
  'ranking.override': { id: 'ranking.override', category: 'session', label: 'Change ranking order', description: 'Can change a session\'s ranking order and cut line after the run, with a reason' },
  // Pools
  'pools.select': { id: 'pools.select', category: 'pools', label: 'Select Pool Items', description: 'Can select which items go into a pool' },
  'pools.lock': { id: 'pools.lock', category: 'pools', label: 'Lock Pools', description: 'Can lock a pool to prevent further changes' },
  'pools.reopen': { id: 'pools.reopen', category: 'pools', label: 'Reopen Pools', description: 'Can reopen a locked pool for modifications' },
  // Item Pools (Lists)
  'lists.view': { id: 'lists.view', category: 'lists', label: 'View Item Pools', description: 'Can view item pools' },
  'lists.create': { id: 'lists.create', category: 'lists', label: 'Create Item Pools', description: 'Can create new item pools' },
  'lists.edit': { id: 'lists.edit', category: 'lists', label: 'Edit Item Pools', description: 'Can edit item pool content and ordering' },
  'lists.move_cutline': { id: 'lists.move_cutline', category: 'lists', label: 'Move Cut Line', description: 'Can adjust the priority cut line position' },
  'lists.publish': { id: 'lists.publish', category: 'lists', label: 'Publish Item Pools', description: 'Can publish item pools' },
  // Criteria
  'criteria.view': { id: 'criteria.view', category: 'criteria', label: 'View Criteria', description: 'Can view criteria and attribute definitions' },
  'criteria.create': { id: 'criteria.create', category: 'criteria', label: 'Create Criteria', description: 'Can create new criteria' },
  'criteria.edit': { id: 'criteria.edit', category: 'criteria', label: 'Edit Criteria', description: 'Can edit existing criteria definitions' },
  'criteria.delete': { id: 'criteria.delete', category: 'criteria', label: 'Delete Criteria', description: 'Can delete criteria' },
  'criteria.select': { id: 'criteria.select', category: 'criteria', label: 'Select Criteria', description: 'Can select which criteria to use for a session' },
  'criteria.weight': { id: 'criteria.weight', category: 'criteria', label: 'Set Criteria Weights', description: 'Can set relative weights for criteria' },
  'criteria.lock': { id: 'criteria.lock', category: 'criteria', label: 'Lock Criteria', description: 'Can lock criteria to prevent further selection changes' },
  'criteria.reopen': { id: 'criteria.reopen', category: 'criteria', label: 'Reopen Criteria', description: 'Can reopen locked criteria for modifications' },
  // Values & Value Ranges
  'values.propose': { id: 'values.propose', category: 'values', label: 'Propose Values', description: 'Can propose values for item attributes' },
  'values.accept': { id: 'values.accept', category: 'values', label: 'Accept Values', description: 'Can accept proposed attribute values' },
  'values.reject': { id: 'values.reject', category: 'values', label: 'Reject Values', description: 'Can reject proposed attribute values' },
  'values.override': { id: 'values.override', category: 'values', label: 'Override Values', description: 'Can override previously accepted values' },
  'values.define_range': { id: 'values.define_range', category: 'values', label: 'Define Value Ranges', description: 'Can define value ranges and scales (e.g., Fibonacci, T-shirt sizes)' },
  'values.approve_range': { id: 'values.approve_range', category: 'values', label: 'Approve Value Ranges', description: 'Can approve proposed value ranges before values are collected' },
  'values.define_scales': { id: 'values.define_scales', category: 'values', label: 'Define Measurement Scales', description: 'Can create and manage measurement scales for attributes' },
  'values.converge': { id: 'values.converge', category: 'values', label: 'Converge Values', description: 'Can facilitate value convergence across multiple proposals' },
  // Attributes
  'attributes.define': { id: 'attributes.define', category: 'attributes', label: 'Define Attributes', description: 'Can define new attribute schemas and types' },
  'attributes.assign_owner': { id: 'attributes.assign_owner', category: 'attributes', label: 'Assign Attribute Owners', description: 'Can assign ownership of attributes to users or groups' },
  // Type lists (migration 931)
  'type_lists.edit': { id: 'type_lists.edit', category: 'attributes', label: 'Edit type lists', description: 'Can change any type list in the workspace (work, launch, investment and custom types) and each type\'s pattern — not only the lists their org unit owns' },
  // Catchball
  'catchball.request': { id: 'catchball.request', category: 'catchball', label: 'Request Catchball', description: 'Can initiate a catchball request to negotiate priorities' },
  'catchball.propose': { id: 'catchball.propose', category: 'catchball', label: 'Submit Proposal', description: 'Can submit a counter-proposal in a catchball negotiation' },
  'catchball.return': { id: 'catchball.return', category: 'catchball', label: 'Return Ball', description: 'Can return the ball with feedback in a catchball negotiation' },
  'catchball.accept_provisional': { id: 'catchball.accept_provisional', category: 'catchball', label: 'Accept Provisionally', description: 'Can provisionally accept a catchball proposal pending final approval' },
  'catchball.accept': { id: 'catchball.accept', category: 'catchball', label: 'Accept Catchball', description: 'Can finally accept a catchball negotiation result' },
  'catchball.reject': { id: 'catchball.reject', category: 'catchball', label: 'Reject Catchball', description: 'Can reject a catchball proposal' },
  'catchball.rollout': { id: 'catchball.rollout', category: 'catchball', label: 'Fan out (Split)', description: 'Can split the ball into parallel child balls when routing policy allows' },
  'catchball.aggregate': { id: 'catchball.aggregate', category: 'catchball', label: 'Aggregate (Rollup)', description: 'Can merge child ball proposals at a Split Node before passing up' },
  'catchball.adjust_path': { id: 'catchball.adjust_path', category: 'catchball', label: 'Adjust Path', description: 'Can rewrite the remaining Path Instance spine while holding the ball (when policy allows)' },
  // Analytics
  'analytics.view': { id: 'analytics.view', category: 'analytics', label: 'View Analytics', description: 'Can view analytics and reports' },
  'analytics.export': { id: 'analytics.export', category: 'analytics', label: 'Export Analytics', description: 'Can export analytics data' },
  // Workflow
  'workflow.create': { id: 'workflow.create', category: 'workflow', label: 'Create Workflows', description: 'Can create new workflow definitions' },
  'workflow.edit': { id: 'workflow.edit', category: 'workflow', label: 'Edit Workflows', description: 'Can edit workflow configuration and templates' },
  'workflow.delete': { id: 'workflow.delete', category: 'workflow', label: 'Delete Workflows', description: 'Can delete workflow definitions' },
  'workflow.archive': { id: 'workflow.archive', category: 'workflow', label: 'Archive Workflows', description: 'Can archive inactive workflows' },
  'workflow.set_default': { id: 'workflow.set_default', category: 'workflow', label: 'Set Default Workflow', description: 'Can set the workspace default workflow' },
  'workflow.assign': { id: 'workflow.assign', category: 'workflow', label: 'Assign Workflow', description: 'Can assign a workflow to a cycle or workspace' },
  'workflow.use': { id: 'workflow.use', category: 'workflow', label: 'Use Workflow', description: 'Can use a workflow when creating cycles' },
  'workflow.manage_templates': { id: 'workflow.manage_templates', category: 'workflow', label: 'Manage Templates', description: 'Can manage workflow template presets (attributes, participants, groups)' },
  // Decision Rights
  'decision_rights.assign': {
    id: 'decision_rights.assign',
    category: 'decision_rights',
    label: 'Assign Decision Rights',
    description: 'Can assign or change who owns a decision right on a cycle (meta-assignment authority)',
  },
  'decision_rights.delegate': {
    id: 'decision_rights.delegate',
    category: 'decision_rights',
    label: 'Delegate Decision Rights',
    description: 'Can delegate a held decision right to another principal within configured constraints',
  },
  'decision_rights.configure': {
    id: 'decision_rights.configure',
    category: 'decision_rights',
    label: 'Configure Decision Rights',
    description: 'Can configure allowed execution modes and delegation constraints for decision rights on a cycle',
  },
  // Admin
  'admin.users': { id: 'admin.users', category: 'admin', label: 'Manage Users', description: 'Can manage user accounts and invitations' },
  'admin.groups': { id: 'admin.groups', category: 'admin', label: 'Manage Groups', description: 'Can manage groups and memberships' },
  'admin.roles': { id: 'admin.roles', category: 'admin', label: 'Manage Roles', description: 'Can manage roles and permission assignments' },
  'admin.policies': { id: 'admin.policies', category: 'admin', label: 'Manage Policies', description: 'Can manage governance policies and rules' },
  'admin.settings': { id: 'admin.settings', category: 'admin', label: 'System Settings', description: 'Can configure system-wide settings' },
  'admin.grant_workspace': { id: 'admin.grant_workspace', category: 'admin', label: 'Grant Workspace Admin', description: 'Can grant workspace-admin access to users or groups for a specific workspace.' },
  'admin.grant_tenant': { id: 'admin.grant_tenant', category: 'admin', label: 'Grant Tenant Admin', description: 'Can grant tenant-admin access across all workspaces in a tenant.' },
};

/**
 * Get all permissions in a category
 */
export function getPermissionsByCategory(category: PermissionCategory): PermissionDefinition[] {
  return Object.values(PERMISSION_DEFINITIONS).filter(p => p.category === category);
}

// ============================================================================
// Scopes
// ============================================================================

/**
 * Types of scopes in the hierarchy (most specific to least specific)
 *
 * Organization and Tenant are the same (top-level). Workspace is below tenant.
 * Spaces are collaboration contexts within a workspace (personal/team/org).
 * Hierarchy: Tenant → Workspace → Space → Department → Team → Project → Process → Session → Item
 */
export type ScopeKind =
  | 'item'         // Most specific: individual item
  | 'session'     // Prioritization session
  | 'process'     // DEGAP process/cycle
  | 'project'     // Project (can span teams)
  | 'team'        // Team within a department
  | 'department'  // Department within organization
  | 'space'       // Collaboration space (personal/team/org-type) within a workspace
  | 'resource'    // Workspace (below tenant; scope_id = workspace_id)
  | 'organization' // Tenant / entire org (same thing; least specific)
  | 'global';      // Workspace-wide wildcard — matches any scope. Not part of
                    // the depth hierarchy below: permissionResolver.ts always
                    // special-cases it with an early return before any
                    // depth/scope-chain comparison runs.

/**
 * Scope hierarchy depth (higher = more specific)
 */
export const SCOPE_DEPTH: Record<ScopeKind, number> = {
  'global': -1,
  'organization': 0,
  'resource': 1,
  'space': 2,
  'department': 3,
  'team': 4,
  'project': 5,
  'process': 6,
  'session': 7,
  'item': 8,
};

/**
 * A specific permission scope instance
 */
export interface PermissionScope {
  kind: ScopeKind;
  id: string;  // Specific ID, or '*' for wildcard (all of this kind)
  name?: string;  // Human-readable name for display
  parentScope?: PermissionScope;  // For building inheritance chains
}

/**
 * Create a scope chain from most specific to least specific
 * 
 * Hierarchy: Organization → Department → Team → Project → Process → Session → Item
 */
export function createScopeChain(
  itemId?: string,
  sessionId?: string,
  processId?: string,
  projectId?: string,
  teamId?: string,
  departmentId?: string,
  organizationId: string = '*'
): PermissionScope {
  // Build from least to most specific
  let currentScope: PermissionScope = { kind: 'organization', id: organizationId };
  
  if (departmentId) {
    currentScope = { kind: 'department', id: departmentId, parentScope: currentScope };
  }
  
  if (teamId) {
    currentScope = { kind: 'team', id: teamId, parentScope: currentScope };
  }
  
  if (projectId) {
    currentScope = { kind: 'project', id: projectId, parentScope: currentScope };
  }
  
  if (processId) {
    currentScope = { kind: 'process', id: processId, parentScope: currentScope };
  }
  
  if (sessionId) {
    currentScope = { kind: 'session', id: sessionId, parentScope: currentScope };
  }
  
  if (itemId) {
    currentScope = { kind: 'item', id: itemId, parentScope: currentScope };
  }
  
  return currentScope;
}

/**
 * Create a scope chain with a simpler interface (for backwards compatibility)
 * Uses the old groupId parameter to map to team
 */
export function createScopeChainSimple(
  itemId?: string,
  sessionId?: string,
  processId?: string,
  groupId?: string,
  organizationId: string = '*'
): PermissionScope {
  return createScopeChain(itemId, sessionId, processId, undefined, groupId, undefined, organizationId);
}

/**
 * Flatten a scope chain into an array (most specific first)
 */
export function flattenScopeChain(scope: PermissionScope): PermissionScope[] {
  const chain: PermissionScope[] = [scope];
  let current = scope.parentScope;
  while (current) {
    chain.push(current);
    current = current.parentScope;
  }
  return chain;
}

// ============================================================================
// Role Assignments
// ============================================================================

/**
 * A role assignment at a specific scope
 */
export interface ScopedRoleAssignment {
  id: string;
  subjectType: 'user' | 'group';
  subjectId: string;
  roleId: string;
  roleLabel: string;
  scopeKind: ScopeKind;
  scopeId: string;
  scopePath?: string[];  // Full path for nested scopes
  inherited: boolean;  // True if this came from a parent scope
  grantedBy: string;
  grantedAt: Date;
  expiresAt?: Date;
}

// ============================================================================
// Effective Permissions
// ============================================================================

/**
 * The resolved permission for a user at a specific scope
 */
export interface EffectivePermission {
  action: PermissionAction;
  granted: boolean;
  sourceScope: PermissionScope;  // Where this permission was granted
  sourceRole: string;  // Which role granted it
  inherited: boolean;  // True if inherited from a parent scope
}

/**
 * Complete permission state for a user at a scope
 */
export interface UserPermissionState {
  userId: string;
  scope: PermissionScope;
  permissions: Map<PermissionAction, EffectivePermission>;
  roles: ScopedRoleAssignment[];
  loadedAt: Date;
}

// ============================================================================
// Groups and Memberships
// ============================================================================

/**
 * Group types for organizational hierarchy
 */
export type GroupType = 'team' | 'department' | 'organization' | 'project' | 'interest-group';

/**
 * A group in the system
 */
export interface Group {
  id: string;
  name: string;
  type: GroupType;
  description?: string;
  parentGroupId?: string;
  metadata?: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * A user's membership in a group
 */
export interface GroupMembership {
  id: string;
  userId: string;
  groupId: string;
  groupName: string;
  groupType: GroupType;
  isPrimary: boolean;
  joinedAt: Date;
}

// ============================================================================
// Policies
// ============================================================================

/**
 * Types of governance policies
 */
export type PolicyRuleType = 
  | 'require_approval'     // Action requires approval from specific role
  | 'restrict_role'        // Restrict who can have a role at this scope
  | 'time_bound'           // Permission only valid during certain times
  | 'require_evidence'     // Action requires supporting evidence
  | 'require_quorum'       // Decision requires minimum participants
  | 'escalation_required'  // Must escalate to higher scope for approval
  | 'audit_required';      // Action must be logged/audited

/**
 * A governance policy
 */
export interface Policy {
  id: string;
  name: string;
  description?: string;
  scopeKind: ScopeKind;
  scopeId: string;
  ruleType: PolicyRuleType;
  ruleConfig: Record<string, unknown>;
  enabled: boolean;
  priority: number;
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Result of policy evaluation
 */
export interface PolicyEvaluationResult {
  allowed: boolean;
  policies: Policy[];  // Policies that were evaluated
  violations: {
    policy: Policy;
    reason: string;
  }[];
  warnings: {
    policy: Policy;
    message: string;
  }[];
}

// ============================================================================
// Permission Check Result
// ============================================================================

/**
 * Complete result of a permission check
 */
export interface PermissionCheckResult {
  allowed: boolean;
  action: PermissionAction;
  scope: PermissionScope;
  effectivePermission?: EffectivePermission;
  policyResult?: PolicyEvaluationResult;
  reason?: string;  // Explanation of why allowed/denied
}

// ============================================================================
// Capability Requirements for Permission Actions (runtime: capability-only)
// ============================================================================

/**
 * Requirement configuration using capability IDs (no legacy role names).
 */
export interface CapabilityRequirement {
  /** Required capability IDs; user needs ANY of these when requiresAny is true */
  requiredCapabilities: CapabilityId[];
  requiresAny: boolean;
  reason?: string;
  isHardRequirement: boolean;
}

/**
 * Map of permission actions to capability requirements.
 * Runtime authorization uses this; resolve role → capabilities once, then check capabilities only.
 */
export const PERMISSION_CAPABILITY_REQUIREMENTS: Partial<Record<PermissionAction, CapabilityRequirement>> = {
  'items.triage.approve': {
    requiredCapabilities: getCapabilitiesForAction('items.triage.approve'),
    requiresAny: true,
    reason: 'Recording a triage decision requires triage authority',
    isHardRequirement: true,
  },
  'items.triage.route': {
    requiredCapabilities: getCapabilitiesForAction('items.triage.route'),
    requiresAny: true,
    reason: 'Routing items to queues requires routing authority',
    isHardRequirement: true,
  },
  'items.triage.attach_relationships': {
    requiredCapabilities: getCapabilitiesForAction('items.triage.attach_relationships'),
    requiresAny: true,
    reason: 'Attaching relationships during triage requires relationship authority',
    isHardRequirement: false,
  },
  'items.manage_ownership': {
    requiredCapabilities: getCapabilitiesForAction('items.manage_ownership'),
    requiresAny: true,
    reason: 'Transferring or delegating item ownership requires ownership authority',
    isHardRequirement: true,
  },
  'criteria.select': {
    requiredCapabilities: getCapabilitiesForAction('criteria.select'),
    requiresAny: true,
    reason: 'Selecting criteria requires criteria authority expertise',
    isHardRequirement: false,
  },
  'criteria.weight': {
    requiredCapabilities: getCapabilitiesForAction('criteria.weight'),
    requiresAny: true,
    reason: 'Setting weights requires criteria authority expertise',
    isHardRequirement: true,
  },
  'criteria.lock': {
    requiredCapabilities: getCapabilitiesForAction('criteria.lock'),
    requiresAny: true,
    reason: 'Locking criteria prevents further changes to selection',
    isHardRequirement: true,
  },
  'criteria.reopen': {
    requiredCapabilities: getCapabilitiesForAction('criteria.reopen'),
    requiresAny: true,
    reason: 'Reopening criteria allows modifications to locked criteria',
    isHardRequirement: true,
  },
  'values.propose': {
    requiredCapabilities: getCapabilitiesForAction('values.propose'),
    requiresAny: true,
    reason: 'Proposing values requires item knowledge',
    isHardRequirement: false,
  },
  'values.accept': {
    requiredCapabilities: getCapabilitiesForAction('values.accept'),
    requiresAny: true,
    reason: 'Accepting values requires authority over the attribute',
    isHardRequirement: false,
  },
  'values.override': {
    requiredCapabilities: getCapabilitiesForAction('values.override'),
    requiresAny: true,
    reason: 'Overriding values requires higher authority',
    isHardRequirement: true,
  },
  'values.define_range': {
    requiredCapabilities: getCapabilitiesForAction('values.define_range'),
    requiresAny: true,
    reason: 'Defining value ranges sets the acceptable scale for an attribute',
    isHardRequirement: true,
  },
  'values.approve_range': {
    requiredCapabilities: getCapabilitiesForAction('values.approve_range'),
    requiresAny: true,
    reason: 'Approving value ranges locks the scale before values are collected',
    isHardRequirement: true,
  },
  'values.define_scales': {
    requiredCapabilities: getCapabilitiesForAction('values.define_scales'),
    requiresAny: true,
    reason: 'Defining measurement scales requires attribute governance authority',
    isHardRequirement: true,
  },
  'values.converge': {
    requiredCapabilities: getCapabilitiesForAction('values.converge'),
    requiresAny: true,
    reason: 'Converging values resolves discrepancies across proposals',
    isHardRequirement: true,
  },
  'session.facilitate': {
    requiredCapabilities: getCapabilitiesForAction('session.facilitate'),
    requiresAny: true,
    reason: 'Facilitating sessions requires trained facilitators',
    isHardRequirement: true,
  },
  'ranking.override': {
    requiredCapabilities: getCapabilitiesForAction('ranking.override'),
    requiresAny: true,
    reason: 'Changing a ranking order after the run is a decision-authority act',
    isHardRequirement: true,
  },
  'session.run_ranking': {
    requiredCapabilities: getCapabilitiesForAction('session.run_ranking'),
    requiresAny: true,
    reason: 'Running ranking tools requires prioritization authority',
    isHardRequirement: true,
  },
  'process.advance_phase': {
    requiredCapabilities: getCapabilitiesForAction('process.advance_phase'),
    requiresAny: true,
    reason: 'Advancing cycle phases is a governance-controlled action',
    isHardRequirement: true,
  },
  'process.legitimacy_advance': {
    requiredCapabilities: getCapabilitiesForAction('process.legitimacy_advance'),
    requiresAny: true,
    reason: 'Legitimacy advancement controls focus, lock, and publish gates',
    isHardRequirement: true,
  },
  'cycle.abort': {
    requiredCapabilities: getCapabilitiesForAction('cycle.abort'),
    requiresAny: true,
    reason: 'Aborting a cycle is destructive and requires authorization',
    isHardRequirement: true,
  },
  'pools.lock': {
    requiredCapabilities: getCapabilitiesForAction('pools.lock'),
    requiresAny: true,
    reason: 'Locking a pool prevents further modifications',
    isHardRequirement: true,
  },
  'pools.reopen': {
    requiredCapabilities: getCapabilitiesForAction('pools.reopen'),
    requiresAny: true,
    reason: 'Reopening a locked pool allows modifications',
    isHardRequirement: true,
  },
  'lists.publish': {
    requiredCapabilities: getCapabilitiesForAction('lists.publish'),
    requiresAny: true,
    reason: 'Publishing Item Pools requires list ownership',
    isHardRequirement: true,
  },
  'process.publish': {
    requiredCapabilities: getCapabilitiesForAction('process.publish'),
    requiresAny: true,
    reason: 'Publishing process results requires authorization',
    isHardRequirement: true,
  },
  'catchball.accept': {
    requiredCapabilities: getCapabilitiesForAction('catchball.accept'),
    requiresAny: true,
    reason: 'Final acceptance of catchball negotiation requires authority',
    isHardRequirement: true,
  },
  'catchball.reject': {
    requiredCapabilities: getCapabilitiesForAction('catchball.reject'),
    requiresAny: true,
    reason: 'Rejecting catchball requires negotiation authority',
    isHardRequirement: true,
  },
};

/**
 * Check if a user has the required capabilities for an action (capability-only; no role names).
 */
export function checkCapabilityRequirement(
  action: PermissionAction,
  userCapabilityIds: CapabilityId[]
): {
  satisfied: boolean;
  requirement?: CapabilityRequirement;
  missingCapabilities?: CapabilityId[];
} {
  const requirement = PERMISSION_CAPABILITY_REQUIREMENTS[action];
  if (!requirement) return { satisfied: true };

  const hasCaps = requirement.requiresAny
    ? requirement.requiredCapabilities.some((c) => userCapabilityIds.includes(c))
    : requirement.requiredCapabilities.every((c) => userCapabilityIds.includes(c));
  if (hasCaps) return { satisfied: true, requirement };

  const missingCapabilities = requirement.requiredCapabilities.filter(
    (c) => !userCapabilityIds.includes(c)
  );
  return { satisfied: false, requirement, missingCapabilities };
}

export function getActionsWithCapabilityRequirements(): PermissionAction[] {
  return Object.keys(PERMISSION_CAPABILITY_REQUIREMENTS) as PermissionAction[];
}

// ============================================================================
// Tool Session Mode Permissions
// ============================================================================

/**
 * Tool session modes determine how tools integrate with the app
 */
export type ToolSessionModePermission = 
  | 'degap_linked'      // Items from app, part of DEGAP cycle
  | 'app_standalone'    // Items from app, NOT part of a cycle
  | 'fully_standalone'; // Paste/import items, export results

/**
 * Configuration for a single tool mode
 */
export interface ToolModeConfig {
  enabled: boolean;
  allowedRoles: string[];
}

/**
 * Tool mode permissions configuration
 */
export interface ToolModePermissions {
  degap_linked: ToolModeConfig;
  app_standalone: ToolModeConfig;
  fully_standalone: ToolModeConfig;
}

/**
 * Default tool mode permissions - all modes enabled, capability-gated at runtime
 */
export const DEFAULT_TOOL_MODE_PERMISSIONS: ToolModePermissions = {
  degap_linked: {
    enabled: true,
    allowedRoles: ['workspace-admin', 'tenant-admin', 'platform-admin', 'system-admin'],
  },
  app_standalone: {
    enabled: true,
    allowedRoles: ['workspace-admin', 'tenant-admin', 'platform-admin', 'system-admin'],
  },
  fully_standalone: {
    enabled: true,
    allowedRoles: ['workspace-admin', 'tenant-admin', 'platform-admin', 'system-admin'],
  },
};

